import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';

import { AppIdentity } from '@evc/platform';
import type { Menu, MenuItemType } from '@evc/web-components';
import { BtnActionEnum } from '@evc/web-components';
import { I18nService } from '@shared/services/i18n/i18n.service';
import { VERSION } from '@shared/version/version';

import { resetBreadcrumbState } from '@app/store/actions/breadcrumb/breadcrumb.action';
import {
  clearSearch,
  loadData,
  setCurrentFolder,
  setSearch,
  setSelectedItems,
} from '@app/store/actions/file/file.action';

@Injectable({
  providedIn: 'root',
})
export class NavigationConfigService {
  readonly #store = inject(Store);
  readonly #i18nService = inject(I18nService);
  readonly #router = inject(Router);

  #helpMenu!: WritableSignal<MenuItemType[]>;
  #appIdentity!: WritableSignal<AppIdentity>;
  #leftbarConfig!: WritableSignal<Menu[]>;

  constructor() {
    this.initConfig();
  }

  public appSettingMenu: MenuItemType[] = [];

  private initConfig(): void {
    this.#leftbarConfig = signal<Menu[]>([
      {
        items: [
          {
            text: this.#i18nService.t$('pages.drive'),
            key: 'pages.drive',
            icon: 'folder-group',
            selected: false,
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: () => this.navigateToPage('/drive'),
            },
          },
          {
            text: this.#i18nService.t$('pages.trash'),
            key: 'pages.trash',
            icon: 'trash-send',
            selected: false,
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: () => this.navigateToPage('/trash'),
            },
          },
        ],
      },
    ]);

    this.#appIdentity = signal<AppIdentity>({
      title: this.#i18nService.t$('pages.drive'),
      logo: '/assets/applications/drive.svg#drive',
      copyright: this.#i18nService.t$('appGeneralInfo.copyright'),
      version: VERSION.semver,
      onTitleClick: () => this.navigateToPage('/drive'),
    });

    this.#helpMenu = signal<MenuItemType[]>(
      ['help_and_support', 'release_notes', 'terms_of_use', 'platform_status'].map((key) => ({
        key,
        text: this.#i18nService.t$(`links.${key}.text`),
        action: {
          type: BtnActionEnum.URL,
          callbackOrUrl: this.#i18nService.t$(`links.${key}.href`),
        },
      })) as MenuItemType[],
    );
  }

  public getAppTitle(): Observable<string> {
    return this.#i18nService.t$('pages.drive');
  }

  public appSearch: (event: any) => void = (event) => this.search(event);

  public updateSelectedTab(route: string): void {
    this.#leftbarConfig().forEach((section) => {
      section.items?.forEach((item) => {
        const baseText: string = item.key.split('.')[1].toLowerCase();
        item.selected = route.includes(baseText);
      });
    });
  }

  public search(search: string): void {
    this.#store.dispatch(setSearch({ search }));
    this.#store.dispatch(loadData({ search }));
  }

  public navigateToPage(page: string): void {
    this.#store.dispatch(clearSearch());
    this.#router.navigate([page]).then(() => {
      this.#store.dispatch(setCurrentFolder({ folder: null }));
      this.#store.dispatch(setSelectedItems({ items: [] }));
      this.#store.dispatch(resetBreadcrumbState());
    });
  }

  public get leftbarConfig(): WritableSignal<Menu[]> {
    return this.#leftbarConfig;
  }

  public set leftbarConfig(value: Menu[]) {
    this.#leftbarConfig.set(value);
  }

  public get appIdentity(): WritableSignal<AppIdentity> {
    return this.#appIdentity;
  }

  public get helpMenu(): WritableSignal<MenuItemType[]> {
    return this.#helpMenu;
  }
}
